import { useState, ChangeEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useParams, useHistory } from 'react-router';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import cn from 'classnames';

import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { UploadInput } from 'UIPackage/MainModule/components/UploadInput';
import { UploadStatus } from 'UIPackage/MainModule/components/UploadStatus';
import { uploadFile } from 'CorePackage/MainModule/common/uploadFile';
import { ROUTE_ADMIN_NEWS } from 'CorePackage/MainModule/routes/routes';
import { NewsStatusEnum } from 'APIPackage/NewsModule/enums/NewsStatusEnum';
import { getAttachmentNews } from 'APIPackage/NewsModule/services/newsApi';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { NewsTypeEnum } from 'APIPackage/NewsModule/enums/NewsTypeEnum';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { compareDates } from 'CorePackage/MainModule/common/formatDate';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getCatalogRolesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IRolesSelectOption } from 'CorePackage/CatalogsModule/interfaces/IRoleSelectOption';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';

import { initialNewsForm } from '../constants/initialNewsForm';
import { mapNewsStatus } from '../common/mapNewsStatus';
import styles from './NewsForm.module.scss';
import { ConfirmNewsModal } from './NewsConfirmModal';
import { CancelModal } from './CancelModal';
import { INewsForm } from '../interfaces/INewsForm';
import { newsTypeOptions } from '../constants/newsTypeOptions';
import { INewsFile } from '../interfaces/INewsFile';
import { newsFormAdd, newsFormGetSingleNews, newsFormUpdate } from '../actions/newsFormActions';
import { newsGetRolesOptions } from '../../MainModule/actions/newsActions';
import { getNewsFormSingleNews } from '../selectors/newsFormSelectors';

interface INewsFormProps {
  rolesOptions: IRolesSelectOption[];
  singleNews?: INewsForm;
}

const NewsFormWithoutStore = ({ rolesOptions, singleNews }: INewsFormProps) => {
  const [files, setFiles] = useState<INewsFile[]>([]);
  const [error, setError] = useState('');
  const [vals, setVals] = useState<INewsForm>(initialNewsForm);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [isShowCancelModal, setIsShowCancelModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [deletedFiles, setDeletedFiles] = useState<number[]>([]);
  const { newsId } = useParams<{ newsId: string }>();
  const history = useHistory();
  const [isFormModified, setIsFormModified] = useState(false);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(newsGetRolesOptions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(newsFormGetSingleNews(newsId));
  }, [dispatch, newsId]);

  const goToNews = useCallback(() => {
    if (!isSubmitted && isFormModified) {
      setIsFormModified(true);
      setIsShowCancelModal(true);

      return;
    }
    history.push(ROUTE_ADMIN_NEWS);
  }, [history, isFormModified, isSubmitted]);

  const onCloseCancelModal = useCallback(() => {
    setIsShowCancelModal(false);
  }, []);

  const onOpenConfirmModal = useCallback((values: INewsForm) => {
    setVals(values);
    setIsShowConfirmModal(true);
  }, []);

  const onCloseConfirmModal = useCallback(() => {
    setIsShowConfirmModal(false);
    setFiles([]);
    setDeletedFiles([]);
  }, []);

  const addingNews = useCallback((values: INewsForm) => dispatch(newsFormAdd(values, history, files)), [
    dispatch,
    files,
    history,
  ]);

  const fileDownload = (id: string, fileId: number, name: string) => async () => {
    try {
      const link: string = await getAttachmentNews(id, fileId);

      saveFile(link, name);
      setError('');
    } catch (err) {
      const res = err as IResponseError;

      setError(res.message || '');
    }
  };

  const removeFile = useCallback(
    (fileName: string) => {
      const fileIndex = files.findIndex(file => file.fileName === fileName);

      setFiles(prev => [...prev.slice(0, fileIndex), ...prev.slice(fileIndex + 1)]);
    },
    [files],
  );

  const handleFormSubmit = useCallback(
    (values: INewsForm) => {
      if (error) {
        if (
          error === 'Можно добавить не больше 3-х файлов' &&
          (singleNews?.attachments?.length || 0) + files.length <= 3
        ) {
          setError('');
        } else {
          return;
        }
      }

      const newsFormValue: INewsForm = {
        type: values.type,
        roles: values.roles,
        title: values.title,
        status: values.status,
        shortText: values.shortText,
        text: setNewsText(values),
        created: values.created,
        published: values.published,
        expired: values.expired,
        attachments: values.attachments,
        countdownStop: values.countdownStop,
        stripHtmlTags: values.type?.value === NewsTypeEnum.ANNOUNCEMENT ? values.stripHtmlTags !== false : undefined,
      };

      if (newsId || !newsFormValue.published) {
        onOpenConfirmModal(newsFormValue);
      } else {
        addingNews(newsFormValue);
      }
      setIsSubmitted(true);
    },
    [addingNews, error, newsId, onOpenConfirmModal],
  );

  const handleUploadInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, fileName?: string) => {
      const attachmentsLength = (singleNews?.attachments && singleNews?.attachments?.length) || 0;

      if (attachmentsLength + files.length > 2) {
        setError('Можно добавить не больше 3-х файлов');

        return;
      }
      uploadFile(e, files, setFiles, fileName);
    },
    [files, singleNews?.attachments],
  );

  useEffect(() => {
    singleNews && setVals(singleNews);
  }, [singleNews]);

  useEffect(() => {
    if (isFormModified) {
      setIsSubmitted(false);
    }
  }, [isFormModified]);

  function setNewsText(values: INewsForm) {
    const shouldStripHtml = values.type?.value === NewsTypeEnum.ANNOUNCEMENT && values.stripHtmlTags !== false;

    if (shouldStripHtml) {
      const tempElement = document.createElement('div');

      tempElement.innerHTML = values.text;

      return tempElement.textContent || values.text;
    }

    return values.text;
  }

  function renderForm({ form, values, handleSubmit }: FormRenderProps<INewsForm>) {
    setIsFormModified(!equal(values, vals) || !!files.length || !!deletedFiles.length);

    function publishNews() {
      const published = new Date();
      const resultCompare = compareDates({ dateLeft: values.published, dateRight: values.expired });

      if (newsId) {
        dispatch(
          newsFormUpdate({
            ...values,
            text: setNewsText(values),
            published: published.toLocaleDateString(),
            expired: resultCompare === 1 || resultCompare === 0 ? '' : values.expired,
          }),
        );
      }
    }

    function expiredNews() {
      if (newsId) {
        dispatch(
          newsFormUpdate({
            ...values,
            text: setNewsText(values),
            expired: new Date().toLocaleDateString(),
          }),
        );
      }
    }

    const makeDeleteAttachmentFile = (id: number) => () => {
      if (newsId) {
        form.change(
          'attachments',
          values.attachments?.filter(item => item.id !== id),
        );
        setDeletedFiles(prev => [...prev, id]);
      }
    };

    const renderControlButtons = () => {
      return newsId ? (
        <>
          <Button
            className="shared-btn"
            color="primary"
            size="medium"
            type="button"
            text="Опубликовать"
            disabled={vals.status === NewsStatusEnum.PUBLISHED}
            onClick={publishNews}
          />
          <Button
            className="shared-btn"
            color="primary"
            size="medium"
            type="button"
            id="unpublishButton"
            text="Убрать из публикации"
            disabled={vals.status === NewsStatusEnum.EXPIRED || vals.status === NewsStatusEnum.CREATED}
            onClick={expiredNews}
          />
        </>
      ) : null;
    };

    const getExpiredValue = () => form.getState().values.expired;
    const getPublishedValue = () => form.getState().values.published;

    const validateDateAnnounce = (value: string) => {
      return form.getState().values.type?.value === NewsTypeEnum.ANNOUNCEMENT
        ? makeRequired('Поле не может быть пустым')(value)
        : undefined;
    };

    return (
      <form onSubmit={handleSubmit}>
        <Row>
          <h1>{newsId ? 'Редактирование новости' : 'Добавить новую новость'}</h1>
        </Row>
        <Row>
          <FieldBlock type="not-space" label="Тип" placementLabel="left" labelSize="col-4" color="black">
            <SelectInputField
              options={newsTypeOptions}
              id="type"
              className="shared-field-small"
              name="type"
              placeholder="Тип"
              validate={makeRequired('Поле обязательно')}
              onChange={typeOption => {
                form.change('stripHtmlTags', typeOption?.value === NewsTypeEnum.ANNOUNCEMENT ? true : undefined);
              }}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            type="not-space"
            label="Для кого видна новость"
            placementLabel="left"
            labelSize="col-4"
            color="black"
          >
            <SelectInputField
              options={rolesOptions}
              className="shared-field-small"
              name="roles"
              placeholder="Для кого видна новость"
              validate={makeRequired('Поле обязательно')}
              isMulti
            />
          </FieldBlock>
        </Row>
        {values.type?.id !== NewsTypeEnum.ANNOUNCEMENT && (
          <Row>
            <FieldBlock type="not-space" label="Заголовок" placementLabel="left" labelSize="col-4" color="black">
              <TextInputField
                className="shared-row-flex"
                name="title"
                placeholder="Заголовок"
                validate={makeRequired('Поле не может быть пустым')}
              />
            </FieldBlock>
          </Row>
        )}
        {values.type?.id !== NewsTypeEnum.ANNOUNCEMENT && (
          <Row>
            <FieldBlock
              type="not-space"
              label="Краткий текст новости"
              placementLabel="left"
              labelSize="col-4"
              color="black"
            >
              <TextInputField
                className="shared-row-flex"
                name="shortText"
                placeholder="Краткий текст новости"
                validate={makeRequired('Поле не может быть пустым')}
              />
            </FieldBlock>
          </Row>
        )}
        {values.type?.id === NewsTypeEnum.ANNOUNCEMENT && (
          <Row>
            <CheckboxField
              type="checkbox"
              id="stripHtmlTags"
              name="stripHtmlTags"
              label="Очищать от html тегов"
              theme="system"
              size="medium"
            />
          </Row>
        )}
        <Row>
          <FieldBlock type="not-space" label="Текст новости" placementLabel="left" labelSize="col-4" color="black">
            <TextareaField
              className={cn('shared-row-flex', styles.NewsArea)}
              name="text"
              id="text"
              placeholder={
                values.type?.id === NewsTypeEnum.ANNOUNCEMENT && values.stripHtmlTags === false
                  ? 'HTML-разметка анонса'
                  : 'Текст'
              }
              validate={makeRequired('Поле не может быть пустым')}
              autoHeight={false}
              withEditor={values.type?.id === NewsTypeEnum.ANNOUNCEMENT ? values.stripHtmlTags !== false : true}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock type="not-space" label="Статус новости" placementLabel="left" labelSize="col-4" color="black">
            <TextInputField className="shared-row-flex" name="status" id="status" format={mapNewsStatus} disabled />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock type="not-space" label="Дата публикации" placementLabel="left" labelSize="col-4" color="black">
            <DatePickerField
              name="published"
              id="published"
              className="shared-field-small"
              validate={composeValidators(
                makeNoLaterThenValidator('Дата публикации не может быть позже даты истечения новости', getExpiredValue),
                validateDateAnnounce,
              )}
            />
          </FieldBlock>
        </Row>
        <Row margin="medium">
          <FieldBlock
            type="not-space"
            label="Дата истечения срока публикации"
            placementLabel="left"
            labelSize="col-4"
            color="black"
          >
            <DatePickerField
              name="expired"
              className="shared-field-small"
              validate={composeValidators(
                makeNoSoonerThenValidator(
                  'Дата истечения не может быть раньше даты публикации новости',
                  getPublishedValue,
                ),
                validateDateAnnounce,
              )}
            />
          </FieldBlock>
        </Row>
        {values.type?.id === NewsTypeEnum.ANNOUNCEMENT && (
          <Row>
            <FieldBlock
              type="not-space"
              label="Дата и время остановки таймера"
              placementLabel="left"
              labelSize="col-4"
              color="black"
            >
              <DatePickerField name="countdownStop" timeFormat />
            </FieldBlock>
          </Row>
        )}
        {values.type?.id !== NewsTypeEnum.ANNOUNCEMENT && (
          <UploadInput
            invalidExtensions={['rtf', 'txt']}
            files={files}
            error={error}
            onChange={handleUploadInputChange}
            setError={setError}
            fileToNews
          />
        )}
        {values.attachments?.length !== undefined &&
          newsId &&
          values.attachments.map(x => (
            <div key={x.id} className="shared-row_small-margin">
              <Row notSpace>
                <div className={styles.FileImg} />
                <div className={styles.FileText}>
                  <span className={styles.FileLink} onClick={fileDownload(newsId, x.id, x.name)}>
                    {x.name}
                  </span>
                  &nbsp;
                  <button className={styles.RemoveButton} type="button" onClick={makeDeleteAttachmentFile(x.id)}>
                    x
                  </button>
                </div>
              </Row>
            </div>
          ))}
        <div className="shared-row">
          <UploadStatus files={files} removeFile={removeFile} setError={setError} />
        </div>
        <div className={styles.Buttons}>
          <Button className="shared-btn" color="primary" size="medium" text="Сохранить" id="saveButton" type="submit" />
          {renderControlButtons()}
          <Button className="shared-btn" size="medium" text="Назад" type="button" onClick={goToNews} />
        </div>
      </form>
    );
  }

  return (
    <div className={styles.Form}>
      <Form<INewsForm> onSubmit={handleFormSubmit} render={renderForm} initialValues={singleNews || initialNewsForm} />
      <ConfirmNewsModal
        visible={isShowConfirmModal}
        files={files}
        deletedFiles={deletedFiles}
        onClose={onCloseConfirmModal}
        values={vals}
        newsId={newsId}
      />
      <CancelModal visible={isShowCancelModal} onClose={onCloseCancelModal} values={vals} />
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  rolesOptions: getCatalogRolesSelectOptions(state),
  singleNews: getNewsFormSingleNews(state),
});

export const NewsForm = connect(mapStateToProps)(NewsFormWithoutStore);
